import { useMemo } from 'react'
import type * as React from 'react'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import type { DrawImagesCategory } from 'tripPlanner/hooks/api/locationImage'
import { useLocationDrawPlaceDetails } from 'tripPlanner/hooks/api/locationImage'
import type { CardOption } from './CardsList'
import Group from 'components/utils/Group'

const IMAGE_WIDTH = 180
const IMAGE_HEIGHT = 108

const imageFrameStyles = css`
  width: ${rem(IMAGE_WIDTH)};
  min-width: ${rem(IMAGE_WIDTH)};
  min-height: ${rem(IMAGE_HEIGHT)};
  align-self: stretch;
  border-radius: ${props => props.theme.borderRadius.L} 0 0 ${props => props.theme.borderRadius.L};
`

const ImageContainer = styled.div`
  ${imageFrameStyles}
  position: relative;
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

const ImageInner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const EmojiContainer = styled(Group)`
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.palette.neutral.default.seven};
  font-size: ${rem(32)};
`

interface Props {
  option: CardOption
  category: DrawImagesCategory
  optionIndex?: number
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}

const InteractiveImageContainer = styled.button.attrs({ type: 'button' })`
  ${imageFrameStyles}
  position: relative;
  padding: 0;
  border: 0;
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.default.seven};
  cursor: pointer;
`

function ImageCard({ option, category, optionIndex, onClick }: Props) {
  const intl = useIntl()
  const locationData = useMemo(() => {
    if (!option.placeSearch) return undefined
    return {
      title: option.title,
      description: option.description,
      emoji: option.emoji,
      placeSearch: option.placeSearch,
    }
  }, [option])

  const { data: drawData, isLoading } = useLocationDrawPlaceDetails(locationData, category)
  const openDetailsLabel = intl.formatMessage(
    {
      defaultMessage: 'Open {title} details',
      id: 'Pmw_RwwOtjqvS9LFYyYu',
      description: 'Aria label for opening trip planner place details from an image card',
    },
    { title: option.title },
  )

  const firstImageId = drawData?.imageIdentifiers?.[0]
  const isUrl = firstImageId?.startsWith('http')

  const renderImageWrapper = (children: React.ReactNode) => {
    if (onClick) {
      return (
        <InteractiveImageContainer
          onClick={onClick}
          data-option-index={optionIndex}
          aria-label={openDetailsLabel}
        >
          {children}
        </InteractiveImageContainer>
      )
    }

    return (
      <ImageContainer>
        {children}
      </ImageContainer>
    )
  }

  if (isLoading) {
    return renderImageWrapper(<LoadingBox floating />)
  }

  if (firstImageId) {
    if (isUrl) {
      return renderImageWrapper(
        <ImageInner>
          <img src={firstImageId} alt={option.title} />
        </ImageInner>,
      )
    }
    return renderImageWrapper(
      <ImageInner>
        <ResponsiveImage
          id={firstImageId}
          aspectRatio="5:3"
          fit="center"
          alt={option.title}
        />
      </ImageInner>,
    )
  }

  if (option.emoji) {
    return renderImageWrapper(
      <EmojiContainer direction="vertical" horizontalAlign="center" verticalAlign="center">
        {option.emoji}
      </EmojiContainer>,
    )
  }

  return null
}

export default ImageCard
